import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import {
  Appbar, Button, Card, Chip, Divider, HelperText, Text, TextInput,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import { NativeBaseProvider } from 'native-base';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { NAS_CONFIG_STATUS } from '../../gql/nas/query';
import {
  SET_NAS_CONFIG, UNSET_NAS_CONFIG,
  TEST_NAS_CONNECTION, TEST_CURRENT_NAS_CONNECTION,
} from '../../gql/nas/mutation';
import { SCREEN_NAMES } from '../../utils/constant';

function ManageNas() {
  const toast = useToast();
  const navigation = useNavigation();

  const { data, refetch } = useQuery(NAS_CONFIG_STATUS);
  const status = data?.nasConfigStatus;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ host: '', username: '', password: '', basePath: '/' });
  const [testResult, setTestResult] = useState(null);

  const [setNasConfig, { loading: saving }] = useMutation(SET_NAS_CONFIG, {
    onCompleted: () => { toast.show('Configuration NAS sauvegardée'); setEditing(false); setTestResult(null); refetch(); },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [unsetNasConfig, { loading: removing }] = useMutation(UNSET_NAS_CONFIG, {
    onCompleted: () => { toast.show('Configuration NAS supprimée'); refetch(); },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [testConnection, { loading: testing }] = useMutation(TEST_NAS_CONNECTION, {
    onCompleted: (d) => setTestResult(d.testNasConnection),
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [testCurrentConnection, { loading: testingCurrent }] = useMutation(TEST_CURRENT_NAS_CONNECTION, {
    onCompleted: (d) => setTestResult(d.testCurrentNasConnection),
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const handleEdit = () => {
    setForm({ host: status?.host ?? '', username: '', password: '', basePath: status?.basePath ?? '/' });
    setTestResult(null);
    setEditing(true);
  };

  const handleTest = () => {
    testConnection({ variables: { host: form.host, username: form.username, password: form.password, basePath: form.basePath } });
  };

  const handleSave = () => {
    setNasConfig({ variables: { host: form.host, username: form.username, password: form.password, basePath: form.basePath } });
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#fff' }}>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content title="Configuration NAS" />
      </Appbar.Header>

      <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
        {/* Status card */}
        <Card>
          <Card.Title title="Connexion NAS" subtitle={status?.configured ? 'Connecté' : 'Non configuré'} />
          <Card.Content>
            {status?.configured ? (
              <>
                <Text variant="bodyMedium">{status.host}</Text>
                <Text variant="bodySmall" style={{ opacity: 0.6 }}>
                  Chemin : {status.basePath}
                </Text>
                <Text variant="bodySmall" style={{ opacity: 0.6 }}>
                  Mis à jour par {status.updatedByName} le {new Date(status.updatedAt).toLocaleDateString('fr-FR')}
                </Text>
              </>
            ) : (
              <Text variant="bodySmall" style={{ opacity: 0.6 }}>Aucun NAS configuré</Text>
            )}
          </Card.Content>
          <Card.Actions>
            {status?.configured && (
              <Button
                onPress={() => testCurrentConnection()}
                loading={testingCurrent}
                icon="connection"
              >
                Tester la connexion
              </Button>
            )}
            <Button onPress={handleEdit} icon="pencil">
              {status?.configured ? 'Modifier' : 'Configurer'}
            </Button>
            {status?.configured && (
              <Button
                onPress={() => unsetNasConfig()}
                loading={removing}
                textColor="red"
                icon="delete"
              >
                Supprimer
              </Button>
            )}
          </Card.Actions>
        </Card>

        {/* Test result banner */}
        {testResult && (
          <Chip
            icon={testResult.success ? 'check-circle' : 'alert-circle'}
            style={{ backgroundColor: testResult.success ? '#e8f5e9' : '#ffebee' }}
          >
            {testResult.success
              ? `Connecté — ${testResult.rootItems} éléments — ${testResult.latencyMs}ms`
              : `Échec : ${testResult.error}`}
          </Chip>
        )}

        {/* Edit form */}
        {editing && (
          <Card>
            <Card.Title title="Identifiants NAS" />
            <Card.Content style={{ gap: 8 }}>
              <TextInput
                label="URL QuickConnect (ex: https://votre-id.quickconnect.to)"
                value={form.host}
                onChangeText={(v) => setForm((p) => ({ ...p, host: v }))}
                autoCapitalize="none"
                keyboardType="url"
              />
              <TextInput
                label="Nom d'utilisateur"
                value={form.username}
                onChangeText={(v) => setForm((p) => ({ ...p, username: v }))}
                autoCapitalize="none"
              />
              <TextInput
                label="Mot de passe"
                value={form.password}
                onChangeText={(v) => setForm((p) => ({ ...p, password: v }))}
                secureTextEntry
              />
              <TextInput
                label="Chemin de base (ex: /Public)"
                value={form.basePath}
                onChangeText={(v) => setForm((p) => ({ ...p, basePath: v }))}
                autoCapitalize="none"
              />
              <HelperText type="info">
                Le mot de passe est chiffré côté serveur et ne transite jamais vers les utilisateurs.
              </HelperText>
            </Card.Content>
            <Card.Actions>
              <Button onPress={() => { setEditing(false); setTestResult(null); }}>Annuler</Button>
              <Button onPress={handleTest} loading={testing} icon="connection">
                Tester
              </Button>
              <Button
                mode="contained"
                onPress={handleSave}
                loading={saving}
                disabled={!testResult?.success}
                icon="content-save"
              >
                Sauvegarder
              </Button>
            </Card.Actions>
          </Card>
        )}

        <Divider style={{ marginVertical: 8 }} />

        {/* Scan shortcut */}
        {status?.configured && (
          <Card>
            <Card.Title title="Scanner un dossier NAS" subtitle="Suggérer automatiquement des médias aux utilisateurs" />
            <Card.Actions>
              <Button
                mode="contained-tonal"
                icon="folder-search"
                onPress={() => navigation.navigate(SCREEN_NAMES.NAS_SCAN)}
              >
                Lancer le scan
              </Button>
            </Card.Actions>
          </Card>
        )}
      </ScrollView>
    </View>
  );
}

export default function ManageNasScreen() {
  return (
    <NativeBaseProvider>
      <ManageNas />
    </NativeBaseProvider>
  );
}
